// ==========================================
// src/components/dashboard/BusinessSettings.js - Business settings management
// ==========================================

import { Clock, MapPin, Phone, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useBusiness } from '../../hooks/useBusiness';


const BusinessSettings = () => {
  const { businessProfile, updateBasicInfo, updateWorkingHours, updateSettings, loading } = useBusiness();
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    businessName: '',
    formattedAddress: '',
    numberOfChairs: '1',
    timezone: 'America/Toronto'
  });
  const [workingHours, setWorkingHours] = useState({});
  const [aiSettings, setAiSettings] = useState({
    greetingMessage: '',
    businessDescription: '',
    specialInstructions: ''
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (businessProfile) {
      setFormData({
        businessName: businessProfile.businessName,
        formattedAddress: businessProfile.formattedAddress,
        numberOfChairs: businessProfile.numberOfChairs,
        timezone: businessProfile.timezone
      });
      setWorkingHours(businessProfile.workingHours);
      setAiSettings({
        greetingMessage: businessProfile.settings?.aiConfig?.greetingMessage || '',
        businessDescription: businessProfile.settings?.aiConfig?.businessDescription || '',
        specialInstructions: businessProfile.settings?.aiConfig?.specialInstructions || ''
      });
    }
  }, [businessProfile]);

  const handleBasicInfoSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    const result = await updateBasicInfo(formData);
    
    if (result.success) {
      setMessage('Basic information updated successfully!');
    } else {
      setMessage(`Error: ${result.error}`);
    }
    
    setSaving(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleWorkingHoursSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    const result = await updateWorkingHours(workingHours);
    
    if (result.success) {
      setMessage('Working hours updated successfully!');
    } else {
      setMessage(`Error: ${result.error}`);
    }
    
    setSaving(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleAiSettingsSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    const result = await updateSettings({
      aiConfig: aiSettings
    });
    
    if (result.success) {
      setMessage('AI settings updated successfully!');
    } else {
      setMessage(`Error: ${result.error}`);
    }
    
    setSaving(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: MapPin },
    { id: 'hours', label: 'Working Hours', icon: Clock },
    { id: 'ai', label: 'AI Assistant', icon: Phone }
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Business Settings</h1>
        <p className="text-gray-600">Manage your business information and preferences</p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.includes('Error') 
            ? 'bg-red-50 text-red-700 border border-red-200' 
            : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          {message}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow border">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="mr-2 h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'basic' && (
            <form onSubmit={handleBasicInfoSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name
                  </label>
                  <input
                    type="text"
                    value={formData.businessName}
                    onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Chairs/Stations
                  </label>
                  <select
                    value={formData.numberOfChairs}
                    onChange={(e) => setFormData({...formData, numberOfChairs: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {[1,2,3,4,5,6,7,8,9,10].map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Address
                </label>
                <input
                  type="text"
                  value={formData.formattedAddress}
                  onChange={(e) => setFormData({...formData, formattedAddress: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="America/Toronto">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                </select>
              </div>
              
              <button
                type="submit"
                disabled={saving}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          )}

          {activeTab === 'hours' && (
            <form onSubmit={handleWorkingHoursSubmit} className="space-y-6">
              <p className="text-gray-600">Set your business operating hours for each day of the week.</p>
              
              <div className="space-y-4">
                {Object.entries(workingHours).map(([day, hours]) => (
                  <div key={day} className="flex items-center space-x-4">
                    <div className="w-20">
                      <label className="block text-sm font-medium text-gray-700 capitalize">
                        {day}
                      </label>
                    </div>
                    <div className="flex-1">
                      {hours === 'closed' ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-500 text-sm">Closed</span>
                          <button
                            type="button"
                            onClick={() => setWorkingHours({...workingHours, [day]: '09:00-17:00'})}
                            className="text-blue-600 hover:text-blue-700 text-sm"
                          >
                            Open this day
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <input
                            type="time"
                            value={hours.split('-')[0]}
                            onChange={(e) => {
                              const endTime = hours.split('-')[1];
                              setWorkingHours({...workingHours, [day]: `${e.target.value}-${endTime}`});
                            }}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <span>to</span>
                          <input
                            type="time"
                            value={hours.split('-')[1]}
                            onChange={(e) => {
                              const startTime = hours.split('-')[0];
                              setWorkingHours({...workingHours, [day]: `${startTime}-${e.target.value}`});
                            }}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => setWorkingHours({...workingHours, [day]: 'closed'})}
                            className="text-red-600 hover:text-red-700 text-sm"
                          >
                            Close
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                type="submit"
                disabled={saving}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          )}

          {activeTab === 'ai' && (
            <form onSubmit={handleAiSettingsSubmit} className="space-y-6">
              <p className="text-gray-600">Customize how your AI assistant interacts with customers.</p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Greeting Message
                </label>
                <textarea
                  value={aiSettings.greetingMessage}
                  onChange={(e) => setAiSettings({...aiSettings, greetingMessage: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Hello! Welcome to [Business Name]. How can I help you today?"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Description
                </label>
                <textarea
                  value={aiSettings.businessDescription}
                  onChange={(e) => setAiSettings({...aiSettings, businessDescription: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe your business, specialties, and what makes you unique..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Instructions
                </label>
                <textarea
                  value={aiSettings.specialInstructions}
                  onChange={(e) => setAiSettings({...aiSettings, specialInstructions: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Any special instructions for the AI (pricing policies, booking rules, etc.)"
                />
              </div>
              
              <button
                type="submit"
                disabled={saving}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessSettings;